import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useGroups } from '@communities/groups-layer/composables/useGroups'

import { useChannelsQueryStore } from '../stores/channels.query-store'
import { useChannelsUIStore } from '../stores/channels.ui-store'

import type {
  ChannelSequenceItem,
  ChannelWithMetadata,
  CreateChannelInput,
  IChannel,
  MoveChannelMutationInput,
  UpdateChannelAccessSettingsInput,
  UpdateChannelInput,
  UpdateChannelMembershipSettingsInput,
} from '../types'

/**
 * Channels Composable - Orchestrates channels data fetching and UI state
 * Provides a clean API for reading channels list and metadata
 *
 * @param groupId - Optional group ID. If not provided, uses the current group from context
 *
 * @example
 * // Use with current group
 * const channels = useChannels()
 *
 * // Use with specific group
 * const groupId = ref('group-123')
 * const channels = useChannels(groupId)
 *
 * // Access channels list
 * const { channels, isLoading, error } = channels.channelsList
 *
 * // Access channels metadata
 * const { metadata, isLoading } = channels.channelsMetadata
 */
export const useChannels = (groupId?: MaybeRefOrGetter<string | undefined>) => {
  // If no groupId provided, get it from the current groups context
  if (!groupId) {
    const { store: groupStore } = useGroups()
    const currentGroupId = computed(() => groupStore.currentGroup?._id)

    if (!currentGroupId.value) {
      console.warn('[useChannels] No groupId provided and no current group found')
    }

    groupId = currentGroupId
  }

  // Initialize stores
  const queryStore = useChannelsQueryStore(groupId)
  const uiStore = useChannelsUIStore()

  // ===== CHANNELS LIST INTERFACE =====
  const getChannelsList = queryStore.channelsListQuery()

  // Prefetch helper for non-community contexts (e.g. share recording from meet)
  // where useAccessControl can't reliably gate the channels query. Writes to
  // the same canonical list cache key so getChannelsList consumers read it
  // immediately. ⚠️ Skips access-control — only call from share-flows whose
  // upstream already filtered to groups the user is a member of. Do not call
  // inside a community route; use getChannelsList there.
  const prefetchChannelsListWithoutAccessControl = (gId: string, locationId: string) =>
    queryStore.prefetchChannelsListWithoutAccessControl(gId, locationId)

  // ===== CHANNELS METADATA INTERFACE =====
  const getChannelsMetadata = queryStore.channelsMetadataQuery()

  // ===== CHANNEL BY ID (factory for mention popovers, etc.) =====
  const getChannelByIdQuery = queryStore.getChannelByIdQuery
  const fetchChannelStatsQuery = queryStore.fetchChannelStatsQuery

  // ===== CHANNELS WITH METADATA COMPUTED =====
  const channelsWithMetadata = computed((): ChannelWithMetadata[] => {
    const channels = getChannelsList.data.value || []
    const metadata = getChannelsMetadata.data.value

    if (!metadata) {
      return channels.map(channel => ({
        ...channel,
        unreadCount: 0,
        managers: [],
      }))
    }

    return channels.map(channel => ({
      ...channel,
      unreadCount: metadata.unreadCounts?.[channel._id || ''] || 0,
      managers: metadata.managers?.[channel._id || ''] || [],
    }))
  })

  // ===== UTILITY FUNCTIONS =====
  const findChannelById = (channelId: string) => {
    return getChannelsList.data.value?.find(channel => channel._id === channelId)
  }

  const findChannelBySlug = (channelSlug: string) => {
    return getChannelsList.data.value?.find(channel => channel.slug === channelSlug)
  }

  // ===== CHANNEL SETTINGS MODAL (UI STORE INTEGRATION) =====
  /**
   * Get the selected channel object from the channel ID stored in UI store
   */
  const selectedChannel = computed(() => {
    const channelId = uiStore.selectedChannelForSettings
    if (!channelId) return null
    return findChannelById(channelId)
  })

  // ===== UTILITY: Mark channel as read by slug =====
  const markChannelAsReadBySlug = async (channelSlug: string) => {
    // Find channel by slug
    const channel = findChannelBySlug(channelSlug)
    if (!channel) {
      throw new Error(`Channel with slug "${channelSlug}" not found`)
    }

    if (!channel._id) {
      throw new Error(`Channel with slug "${channelSlug}" has no _id`)
    }

    // Call the mutation
    return queryStore.markChannelAsReadMutation.mutateAsync(channel._id)
  }

  // ===== MUTATION WRAPPERS =====
  /**
   * Create a new channel
   */
  const createChannel = async (data: CreateChannelInput): Promise<IChannel> => {
    return queryStore.createChannelMutation.mutateAsync(data)
  }

  /**
   * Update an existing channel
   */
  const updateChannel = async (data: UpdateChannelInput): Promise<IChannel> => {
    return queryStore.updateChannelMutation.mutateAsync(data)
  }

  const updateChannelAccessSettings = async (data: UpdateChannelAccessSettingsInput): Promise<IChannel> => {
    return queryStore.updateChannelAccessSettingsMutation.mutateAsync(data)
  }

  const updateChannelMembershipSettings = async (data: UpdateChannelMembershipSettingsInput): Promise<IChannel> => {
    return queryStore.updateChannelMembershipSettingsMutation.mutateAsync(data)
  }

  const joinPaidChannel = async (channelId: string, orderId: string, orderSourceId: string) => {
    return queryStore.joinPaidChannelMutation.mutateAsync({ channelId, orderId, orderSourceId })
  }

  /**
   * Update channel visibility (private/public) via dedicated endpoint
   */
  const updateChannelVisibility = async (channelId: string, visibility: IChannel['visibility']): Promise<void> => {
    return queryStore.updateChannelVisibilityMutation.mutateAsync({ channelId, visibility })
  }

  /**
   * Delete a channel
   */
  const deleteChannel = async (channelId: string): Promise<void> => {
    return queryStore.deleteChannelMutation.mutateAsync(channelId)
  }

  /**
   * Leave a channel
   */
  const leaveChannel = async (channelId: string): Promise<void> => {
    return queryStore.leaveChannelMutation.mutateAsync(channelId)
  }

  /**
   * Mark a channel as read
   */
  const markChannelAsRead = async (channelId: string): Promise<void> => {
    return queryStore.markChannelAsReadMutation.mutateAsync(channelId)
  }

  const updateChannelSequence = async (groupId: string, updatedChannelSequence: ChannelSequenceItem[]): Promise<unknown> => {
    return queryStore.updateChannelSequenceMutation.mutateAsync({ groupId, updatedChannelSequence })
  }

  /**
   * Reorder channels with optimistic update (handled in query store), then sync with server.
   */
  const reorderChannels = async (reorderedChannels: IChannel[]): Promise<void> => {
    const gId = toValue(groupId)
    if (!gId) return

    const channelsOnly = reorderedChannels.filter(c => !c.isDivider)
    const payload: ChannelSequenceItem[] = channelsOnly.map((c, i) => ({
      id: c._id ?? '',
      channelName: c.name,
      sequence: i + 1,
    }))

    try {
      await queryStore.updateChannelSequenceMutation.mutateAsync({
        groupId: gId,
        updatedChannelSequence: payload,
        reorderedChannels,
      })
    } catch (error) {
      console.error('[useChannels] Failed to reorder channels:', error)
    }
  }

  const moveChannel = async (placement: MoveChannelMutationInput): Promise<void> => {
    try {
      await queryStore.moveChannelMutation.mutateAsync(placement)
    } catch (error) {
      console.error('[useChannels] Failed to move channel:', error)
      throw error
    }
  }

  // ===== PUBLIC API =====
  return {
    // Channels list interface
    getChannelsList,

    // Prefetch channels list (non-community contexts that bypass access control)
    prefetchChannelsListWithoutAccessControl,

    // Channels metadata interface
    getChannelsMetadata,

    // Channel by ID (e.g. for mention popovers)
    getChannelByIdQuery,

    fetchChannelStatsQuery,

    // Channels with metadata (enriched data)
    channelsWithMetadata,

    // Utility functions
    findChannelById,
    findChannelBySlug,

    // Mutations (wrapped to hide TanStack Query internals)
    createChannel,
    updateChannel,
    updateChannelAccessSettings,
    updateChannelMembershipSettings,
    joinPaidChannel,
    updateChannelVisibility,
    deleteChannel,
    leaveChannel,
    markChannelAsRead,
    markChannelAsReadBySlug, // Convenience method
    updateChannelSequence,
    reorderChannels,
    moveChannel,

    // Channel settings modal state and methods (from UI store)
    channelSettings: {
      show: computed(() => uiStore.showChannelSettings),
      type: computed(() => uiStore.channelSettingsType),
      channel: selectedChannel, // Computed channel object from ID
      openChannelSettings: uiStore.openChannelSettings,
      openCreateChannel: uiStore.openCreateChannel,
      closeChannelSettings: uiStore.closeChannelSettings,
    },

    // UI state management (read-only focused)
    ui: {
      currentChannelId: computed(() => uiStore.currentChannelId),
      setCurrentChannel: uiStore.setCurrentChannel,
      clearSelection: uiStore.clearSelection,
      reset: uiStore.reset,
      setChannelLastReadTimestamp: uiStore.setChannelLastReadTimestamp,
      getChannelLastReadTimestamp: uiStore.getChannelLastReadTimestamp,
      clearChannelLastReadTimestamp: uiStore.clearChannelLastReadTimestamp,
    },
  }
}
